import { clampNumber } from './math.utils';

export function clampPercentage(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return clampNumber(value, 0, 100);
}

export function distributePercentageEvenly(parts: number, total: number = 100): number[] {
  if (!Number.isFinite(parts) || parts <= 0) return [];

  const safeParts = Math.floor(parts);
  const base = Math.floor(total / safeParts);
  const remainder = total % safeParts;

  return Array.from({ length: safeParts }, (_, index) => base + (index < remainder ? 1 : 0));
}

export function distributeWeightedPercentage(weights: number[], total: number = 100): number[] {
  if (weights.length === 0) return [];
  if (weights.length === 1) return [total];

  const weightSum = weights.reduce((sum, weight) => sum + Math.max(0, weight), 0);
  if (weightSum <= 0) return distributePercentageEvenly(weights.length, total);

  const shares = weights.map((weight) => (Math.max(0, weight) / weightSum) * total);
  const baseValues = shares.map((share) => Math.floor(share));
  let remainder = total - baseValues.reduce((sum, value) => sum + value, 0);

  const sorted = shares
    .map((share, index) => ({ index, fraction: share - baseValues[index]! }))
    .sort((left, right) => right.fraction - left.fraction || left.index - right.index);

  for (const candidate of sorted) {
    if (remainder <= 0) break;
    baseValues[candidate.index] = (baseValues[candidate.index] ?? 0) + 1;
    remainder -= 1;
  }

  return baseValues;
}

export function resolveEffectiveAssessments<T extends { schoolSubjectId: number | null }>(
  assessments: T[],
  subjectId: number | null,
  usesSubjectSpecificAssessments: boolean,
): { assessments: T[]; isSubjectSpecificContext: boolean } {
  if (subjectId == null) {
    return {
      assessments: assessments.filter((assessment) => assessment.schoolSubjectId == null),
      isSubjectSpecificContext: false,
    };
  }

  const subjectSpecific = assessments.filter((assessment) => assessment.schoolSubjectId === subjectId);
  if (usesSubjectSpecificAssessments && subjectSpecific.length > 0) {
    return { assessments: subjectSpecific, isSubjectSpecificContext: true };
  }

  return {
    assessments: assessments.filter((assessment) => assessment.schoolSubjectId == null),
    isSubjectSpecificContext: false,
  };
}
